// User detail (SM-754): the immutable user id (monospace + copy), the
// wallet, status changes (SUSPENDED / CLOSED need the typed phrase
// "<STATUS> <user_id>"), and the user's adjustments, launch codes and
// audit trail.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AdjustmentsTable } from "@/components/domain/AdjustmentsTable";
import { AuditTable } from "@/components/domain/AuditTable";
import { StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { CopyButton, Empty, KV, Mono, PageHeader, ReadOnlyHint, TableWrap } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { api } from "@/lib/api";
import { statusPhrase } from "@/lib/confirm";
import { ACCOUNT_STATUSES } from "@/lib/constants";
import { fmtTime } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import { useCanMutate } from "@/lib/session";
import type { User } from "@/lib/types";

export const Route = createFileRoute("/_app/users/$id")({
  component: UserDetail,
});

function UserDetail() {
  const { id } = Route.useParams();
  const user = useQuery({ queryKey: keys.user(id), queryFn: () => fetchers.user(id) });
  const adjustments = useQuery({ queryKey: keys.adjustments(id), queryFn: () => fetchers.adjustments(id) });
  const codes = useQuery({ queryKey: keys.launchCodes(id), queryFn: () => fetchers.launchCodes(id) });
  const auditParams = { subject_kind: "user", subject_id: id, limit: "50" };
  const audit = useQuery({ queryKey: keys.audit(auditParams), queryFn: () => fetchers.audit(auditParams) });

  if (user.isLoading) return <Skeleton lines={8} />;
  if (user.error || !user.data) return <ErrorBanner error={user.error ?? new Error("User not found.")} />;
  const u = user.data;

  return (
    <div className="space-y-5">
      <PageHeader
        title={u.display_name || "Unnamed user"}
        subtitle={
          <Link to="/users" className="t-mute hover:underline">
            ← All users
          </Link>
        }
        actions={
          <>
            <Link to="/balance" search={{ user: u.user_id }} className="btn btn-ghost btn-sm">
              Adjust balance
            </Link>
            <Link to="/launcher" search={{ user: u.user_id }} className="btn btn-ghost btn-sm">
              Launch code
            </Link>
            <Link to="/transactions" search={{ user_id: u.user_id }} className="btn btn-ghost btn-sm">
              Transactions
            </Link>
            <Link to="/requests" search={{ user_id: u.user_id }} className="btn btn-ghost btn-sm">
              Requests
            </Link>
          </>
        }
      />

      <div className="card">
        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">User id (immutable)</div>
        <div className="flex flex-wrap items-center gap-2">
          <Mono className="text-lg font-semibold text-t-1">
            {u.user_id}
          </Mono>
          <CopyButton value={u.user_id} label="Copy id" />
        </div>
        <div className="mt-4">
          <KV
            items={[
              ["Status", <StatusBadge status={u.account_status} />],
              ["Available balance", <span className="mono text-base font-semibold">{u.available_balance}</span>],
              ["Client", <Mono>{u.client_id}</Mono>],
              ["Created by", u.created_by],
              ["Created", fmtTime(u.created_at)],
              ["Updated", fmtTime(u.updated_at)],
            ]}
          />
        </div>
      </div>

      <StatusChange user={u} />

      <SectionCard title="Balance adjustments">
        <ErrorBanner error={adjustments.error} />
        <AdjustmentsTable rows={adjustments.data ?? []} />
      </SectionCard>

      <SectionCard title="Launch codes (hint only)">
        <ErrorBanner error={codes.error} />
        {(codes.data ?? []).length === 0 ? (
          <Empty>No launch codes issued for this user.</Empty>
        ) : (
          <TableWrap>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Hint</th>
                  <th>State</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>Consumed</th>
                  <th>Rejected code</th>
                </tr>
              </thead>
              <tbody>
                {(codes.data ?? []).map((c) => (
                  <tr key={c.code_hint + c.issued_at}>
                    <td className="mono">{c.code_hint}</td>
                    <td>
                      <StatusBadge status={c.state} />
                    </td>
                    <td>{fmtTime(c.issued_at)}</td>
                    <td>{fmtTime(c.expires_at)}</td>
                    <td>{fmtTime(c.consumed_at)}</td>
                    <td className="mono">{c.rejected_code ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </SectionCard>

      <SectionCard title="Audit">
        <ErrorBanner error={audit.error} />
        <AuditTable rows={audit.data ?? []} emptyText="No audit events for this user." />
      </SectionCard>
    </div>
  );
}

function StatusChange({ user }: { user: User }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>(user.account_status === "ACTIVE" ? "SUSPENDED" : "ACTIVE");
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const phrase = statusPhrase(status, user.user_id);
  const reasonOk = reason.trim().length >= 3;

  const send = async (confirm: string) => {
    await api<User>(`/users/${encodeURIComponent(user.user_id)}/status`, {
      method: "PUT",
      body: { account_status: status, reason: reason.trim(), confirm },
    });
    setReason("");
    await Promise.all([
      qc.invalidateQueries({ queryKey: keys.user(user.user_id) }),
      qc.invalidateQueries({ queryKey: ["mock", "users"] }),
      qc.invalidateQueries({ queryKey: ["mock", "audit"] }),
    ]);
  };

  return (
    <SectionCard title="Account status">
      {!canMutate && <ReadOnlyHint />}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!reasonOk || status === user.account_status) return;
          if (phrase) {
            setOpen(true);
            return;
          }
          setBusy(true);
          setError(null);
          try {
            await send("");
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={!canMutate || busy}>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-end">
            <Field label="New status">
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {ACCOUNT_STATUSES.map((s) => (
                  <option key={s} value={s} disabled={s === user.account_status}>
                    {s}
                    {s === user.account_status ? " (current)" : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reason (required)" error={reason && !reasonOk ? "At least 3 characters." : null}>
              <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="test case this status is for" />
            </Field>
            <Button type="submit" variant={phrase ? "danger" : "primary"} loading={busy} disabled={!reasonOk || status === user.account_status}>
              Set {status}
            </Button>
          </div>
        </fieldset>
        <ErrorBanner error={error} className="mt-3" />
      </form>
      <ConfirmDialog
        open={open}
        title={`Set ${user.user_id} to ${status}?`}
        consequence={
          status === "SUSPENDED" ? (
            <>
              GetBalance and every Deduct for this user will answer <b>403 USER_DISABLED</b> (recorded FAILED); a launch
              exchange answers <b>account_status SUSPENDED</b> so Smart Market refuses the session. Credits still apply.
            </>
          ) : (
            <>
              Every money call for this user — Deduct, Credit and GetBalance — will answer <b>403 USER_DISABLED</b>; a
              launch exchange answers <b>account_status CLOSED</b>. Reversible only by setting the user ACTIVE again.
            </>
          )
        }
        phrase={phrase}
        confirmLabel={`Set ${status}`}
        onConfirm={send}
        onClose={() => setOpen(false)}
      />
    </SectionCard>
  );
}
